import { useMemo } from 'react'

import { IconBullseyeRegular, IconDownload, IconUpload } from '@smartbear/swagger-portal-component-library'

import { CommonSearchResultTile, HighlightedTitle } from '../CommonSearchResultTile'
import { getMethodDisplayName } from '../helpers'
import { isAsyncApi, isDefinitionMarkdown } from '~/portal/components/SearchModal/helpers'
import { OPERATION_METHOD } from '~/portal/types/content'
import { SearchResult } from '~/portal/types/search'
import { buildOperationHash } from '~/portal/utils/api'
import { getOperationCssVar } from '~/portal/utils/shared/operationColor'

interface OperationSearchResultTileProps {
  result: SearchResult
  onClose: () => void
  tileKey: string
}

export const OperationSearchResultTile = ({ result, onClose, tileKey }: OperationSearchResultTileProps) => {
  const url = useMemo(() => {
    if (isDefinitionMarkdown(result) && result.componentAnchorId) {
      return `/${result.product.slug}/${result.href}#${encodeURIComponent(result.componentAnchorId)}`
    }
    if (isAsyncApi(result)) {
      return result.operationId
        ? `/${result.product.slug}/${result.href}${buildOperationHash(result)}`
        : `/${result.product.slug}/${result.href}#/default/${result.asyncChannelName}`
    }
    const operationHash = buildOperationHash(result)
    return `/${result.product.slug}/${result.href}${operationHash}`
  }, [result])

  const icon = useMemo(() => {
    if (result.method === OPERATION_METHOD.SUB) {
      return <IconDownload width={16} height={16} />
    }
    if (result.method === OPERATION_METHOD.PUB) {
      return <IconUpload width={16} height={16} />
    }
    return <IconBullseyeRegular />
  }, [result.method])

  const titleContent = useMemo(() => {
    if (result.method) {
      return (
        <div>
          <span style={{ color: getOperationCssVar(result.method) }} className="sp-operation-method font-semibold">
            {getMethodDisplayName(result.method)}
          </span>
          {' / '}
          <HighlightedTitle titleSnippet={result.titleSnippet} removeLeadingSlash={!result.summary} />
        </div>
      )
    }
    return <HighlightedTitle titleSnippet={result.titleSnippet} />
  }, [result.method, result.titleSnippet, result.summary])

  return (
    <CommonSearchResultTile
      result={result}
      onClose={onClose}
      tileKey={tileKey}
      icon={icon}
      url={url}
      titleContent={titleContent}
    />
  )
}
